// Roadmap — 4-phase timeline (shipped → now → next → planned).
// "Now" phase highlighted with copper glow + lifted bg.
// Source canonical: design-system/project/homepage/index.html § Roadmap.

type PhaseStatus = 'shipped' | 'now' | 'next' | 'planned';

const PHASES: { q: string; t: string; s: PhaseStatus; body: string; items: string[] }[] = [
  {
    q: 'Q4 · 2025', t: 'Roster · shipped', s: 'shipped',
    body: 'Three roles in production at pilot customers: listings, support, GST. Eval framework + gate CI operational.',
    items: ['Priya · listings (188 runs)', 'Arjun · support (1.2k runs)', 'Meera · GST (3.4k runs)'],
  },
  {
    q: 'Q1 · 2026', t: 'Pricing + workspace · now', s: 'now',
    body: 'Two more roles in beta interviews. Billing, workspace SSO, and self-serve onboarding land for design partners.',
    items: ['Kabir + Neha in beta', 'Billing + workspace SSO', 'Self-serve onboarding'],
  },
  {
    q: 'Q2 · 2026', t: 'New roles · in lab', s: 'next',
    body: 'Rohan (inventory ops) finishes training. Two more candidates start interviews. Roster grows from 6 to 9.',
    items: ['Rohan ships', '2 new candidates', 'Roster expands to 9'],
  },
  {
    q: 'Q3 · 2026', t: 'Open the roster · marketplace', s: 'planned',
    body: 'Third parties publish their own roles on Airgent. Bring-your-own skills and schemas. Public API + agent SDK.',
    items: ['Role marketplace', 'Bring-your-own skills', 'Public API & SDK'],
  },
];

const DOT_COLOR: Record<PhaseStatus, string> = {
  shipped: '#4ADE80',
  now:     '#D4835E',
  next:    '#E8A384',
  planned: '#908A82',
};

const LABEL: Record<PhaseStatus, string> = {
  shipped: 'SHIPPED',
  now:     'IN PROGRESS',
  next:    'IN LAB',
  planned: 'PLANNED',
};

// Per-status visual treatment — gradient bg + border + glow.
// Each status reads at a glance without needing to read the label.
const STATUS_STYLE: Record<PhaseStatus, { bg: string; border: string; glow: string; titleColor?: string }> = {
  shipped: {
    bg: 'linear-gradient(160deg, rgba(74,222,128,0.14) 0%, rgba(74,222,128,0.04) 100%)',
    border: '1px solid rgba(74,222,128,0.35)',
    glow: '0 0 24px -8px rgba(74,222,128,0.30)',
  },
  now: {
    bg: 'linear-gradient(160deg, rgba(212,131,94,0.20) 0%, rgba(212,131,94,0.06) 100%)',
    border: '1px solid rgba(212,131,94,0.50)',
    glow: '0 0 32px -6px rgba(212,131,94,0.45)',
  },
  next: {
    bg: 'linear-gradient(160deg, rgba(232,163,132,0.10) 0%, rgba(232,163,132,0.02) 100%)',
    border: '1px dashed rgba(232,163,132,0.35)',
    glow: '0 0 18px -10px rgba(232,163,132,0.20)',
  },
  planned: {
    bg: 'linear-gradient(160deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    glow: 'none',
  },
};

export default function Roadmap() {
  return (
    <section
      id="roadmap"
      style={{
        padding: '96px 40px',
        background: 'linear-gradient(180deg, #161514 0%, #1A1918 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Soft copper glow under the "now" phase */}
      <div style={{
        position: 'absolute', bottom: 0, left: '40%', width: 600, height: 400, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, rgba(212,131,94,0.10) 0%, transparent 70%)',
      }}/>

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <span style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: '#D4835E',
        }}>The arc</span>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 48, fontWeight: 400,
          fontVariationSettings: "'opsz' 96",
          letterSpacing: '-0.02em', lineHeight: 1.1,
          color: '#F5F4F2',
          margin: '10px 0 14px', maxWidth: 820,
        }}>
          We're in the middle of building this.
        </h2>
        <p style={{
          fontFamily: 'Inter Tight, system-ui, sans-serif',
          fontSize: 16, lineHeight: 1.6,
          color: '#C7C3BD',
          maxWidth: 720, margin: '0 0 40px',
        }}>
          Roster shipping. Pricing landing next. Marketplace open by Q3. Founding seats at every step.
        </p>

        {/* DESKTOP / TABLET timeline grid */}
        <div className="lg-only" style={{ position: 'relative' }}>
          {/* Connecting timeline line — gradient through all 4 phases */}
          <div className="roadmap-line" style={{
            position: 'absolute', top: 28, left: '10%', right: '10%', height: 2,
            background: 'linear-gradient(90deg, #4ADE80 0%, #4ADE80 22%, #D4835E 22%, #D4835E 52%, rgba(212,131,94,0.45) 52%, rgba(212,131,94,0.45) 78%, rgba(255,255,255,0.10) 78%, rgba(255,255,255,0.10) 100%)',
          }}/>

          {/* Phase cards */}
          <div className="roadmap-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {PHASES.map((p, i) => {
              const dot = DOT_COLOR[p.s];
              return (
                <div key={i} style={{ position: 'relative', paddingTop: 50 }}>
                  {/* Dot on the timeline */}
                  <div style={{
                    position: 'absolute', top: 22, left: 'calc(50% - 7px)',
                    width: 14, height: 14, borderRadius: 9999,
                    background: dot,
                    border: '3px solid #1A1918',
                    boxShadow: `0 0 0 2px ${dot}`,
                  }}/>
                  {/* Quarter label above the card */}
                  <div style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: 10, color: '#908A82',
                    letterSpacing: '0.08em', textAlign: 'center',
                    marginBottom: 8,
                  }}>
                    {p.q}
                  </div>
                  {/* Card — per-status gradient bg + border + glow */}
                  <div style={{
                    padding: 20, borderRadius: 12,
                    background: STATUS_STYLE[p.s].bg,
                    border: STATUS_STYLE[p.s].border,
                    boxShadow: STATUS_STYLE[p.s].glow,
                    opacity: p.s === 'planned' ? 0.85 : 1,
                  }}>
                    <span style={{
                      fontFamily: 'IBM Plex Mono, monospace',
                      fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.1em', color: dot,
                    }}>
                      {LABEL[p.s]}
                    </span>
                    <div style={{
                      fontFamily: 'Fraunces, serif',
                      fontSize: 18, fontWeight: 500,
                      color: '#F5F4F2',
                      marginTop: 6, marginBottom: 8, lineHeight: 1.2,
                    }}>
                      {p.t}
                    </div>
                    <div style={{
                      fontFamily: 'Inter Tight, system-ui, sans-serif',
                      fontSize: 12.5, color: '#C7C3BD',
                      lineHeight: 1.55, marginBottom: 12,
                    }}>
                      {p.body}
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
                      {p.items.map(x => (
                        <li key={x} style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#908A82' }}>— {x}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* MOBILE: 2x2 grid of all 4 phase cards — everything visible at once.
            Each card uses status-specific gradient + border + glow so shipped /
            now / next / planned read at a glance. */}
        <div className="sm-only roadmap-mobile-grid">
          {PHASES.map((p, i) => {
            const dot = DOT_COLOR[p.s];
            const sty = STATUS_STYLE[p.s];
            return (
              <div
                key={i}
                className="roadmap-mini-card"
                style={{
                  background: sty.bg,
                  border: sty.border,
                  boxShadow: sty.glow,
                  opacity: p.s === 'planned' ? 0.85 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 6 }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#908A82', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{p.q}</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', color: dot, whiteSpace: 'nowrap' }}>● {LABEL[p.s]}</span>
                </div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: 15, fontWeight: 500, color: '#F5F4F2', marginBottom: 6, lineHeight: 1.2 }}>{p.t}</div>
                <div style={{ fontFamily: 'Inter Tight, system-ui, sans-serif', fontSize: 11.5, color: '#C7C3BD', lineHeight: 1.45 }}>{p.body}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
